"use client";

import { useRef, useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type ActBest,
  type ActSuperscore,
  type ActTest,
  type ActVersion,
  actComposite,
  actStem,
  actSuperscore,
  isActScore,
} from "@/lib/tool-kit/grades/act";

/** English strings. A translated content file overrides these through `ui`. */
export const ACT_SCORE_CALCULATOR_STRINGS = {
  testsHeading: "Your ACT tests",
  testLabel: "Test {n}",
  testWithDate: "{n} ({date})",
  dateLabel: "Test date (optional)",
  versionLabel: "Version",
  versionsHint:
    "Enhanced: online National tests from April 2025 and every test from September 2025. Legacy: earlier tests, whose composite included science.",
  versions: {
    enhanced: "Enhanced — English, math, reading",
    legacy: "Legacy — all four sections",
  },
  sections: {
    english: "English",
    math: "Math",
    reading: "Reading",
    science: "Science (optional)",
  },
  removeTest: "Remove",
  removeTestAria: "Remove test {n}",
  addTest: "Add another test",
  skipped:
    "Test {n} is left out until English, math and reading are whole numbers from 1 to 36 — and science too, if you enter it.",
  results: "Result",
  superscore: "ACT superscore",
  bestEnglish: "Best English",
  bestMath: "Best math",
  bestReading: "Best reading",
  bestScience: "Best science",
  fromTest: "{score} · test {n}",
  legacySuperscore: "Four-section superscore, the rule before September 2025",
  working: "({english} + {math} + {reading}) ÷ 3 = {average} → {composite}",
  tableHeading: "Each test",
  colTest: "Test",
  colVersion: "Version",
  colComposite: "Composite",
  colStem: "STEM",
  versionShort: {
    enhanced: "Enhanced",
    legacy: "Legacy",
  },
  needsScience: "Needs science",
  enterTests:
    "Enter English, math and reading scores from 1 to 36 for at least one test.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary:
    "ACT superscore {composite} (English {english}, math {math}, reading {reading})",
} as const;

type Strings = typeof ACT_SCORE_CALCULATOR_STRINGS;
type Format = ReturnType<typeof useToolNumber>;
type Section = "english" | "math" | "reading" | "science";
const SECTIONS: readonly Section[] = ["english", "math", "reading", "science"];

interface TestRow {
  id: number;
  date: string;
  version: ActVersion;
  english: string;
  math: string;
  reading: string;
  science: string;
}

const INITIAL_ROWS: TestRow[] = [
  {
    id: 1,
    date: "",
    version: "legacy",
    english: "24",
    math: "27",
    reading: "25",
    science: "26",
  },
  {
    id: 2,
    date: "",
    version: "enhanced",
    english: "27",
    math: "26",
    reading: "29",
    science: "",
  },
];

/** The row as an ACT test, or null when a required score is not 1–36. */
function readTest(row: TestRow): ActTest | null {
  const english = parseNumberInput(row.english);
  const math = parseNumberInput(row.math);
  const reading = parseNumberInput(row.reading);
  const science =
    row.science.trim() === "" ? null : parseNumberInput(row.science);
  if (!(isActScore(english) && isActScore(math) && isActScore(reading))) {
    return null;
  }
  if (row.science.trim() !== "" && !isActScore(science)) {
    return null;
  }
  return { version: row.version, english, math, reading, science };
}

/** A user-entered date for display. Only called once someone has typed one. */
function formatDate(value: string, locale: string): string {
  const parsed = new Date(`${value}T00:00:00Z`);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(parsed);
}

function SuperscorePanel({
  superscore,
  label,
  strings,
  fmt,
}: {
  superscore: ActSuperscore;
  label: (index: number) => string;
  strings: Strings;
  fmt: Format;
}) {
  const best = (entry: ActBest) =>
    t(strings.fromTest, { score: fmt(entry.score), n: label(entry.test) });
  const { english, math, reading, science } = superscore;
  return (
    <>
      <StatGrid>
        <StatCard
          emphasis
          label={strings.superscore}
          value={fmt(superscore.composite)}
        />
        <StatCard label={strings.bestEnglish} value={best(english)} />
        <StatCard label={strings.bestMath} value={best(math)} />
        <StatCard label={strings.bestReading} value={best(reading)} />
        {science ? (
          <StatCard label={strings.bestScience} value={best(science)} />
        ) : null}
        {superscore.legacyComposite === null ? null : (
          <StatCard
            label={strings.legacySuperscore}
            value={fmt(superscore.legacyComposite)}
          />
        )}
      </StatGrid>
      <p className="rounded-lg border border-border bg-card p-3 font-mono text-muted-foreground text-sm">
        {t(strings.working, {
          english: fmt(english.score),
          math: fmt(math.score),
          reading: fmt(reading.score),
          average: fmt((english.score + math.score + reading.score) / 3, {
            maximumFractionDigits: 2,
          }),
          composite: fmt(superscore.composite),
        })}
      </p>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copyResult}
        small
        text={t(strings.summary, {
          composite: fmt(superscore.composite),
          english: fmt(english.score),
          math: fmt(math.score),
          reading: fmt(reading.score),
        })}
      />
    </>
  );
}

function TestEditor({
  row,
  index,
  canRemove,
  onChange,
  onRemove,
  strings,
}: {
  row: TestRow;
  index: number;
  canRemove: boolean;
  onChange: (patch: Partial<TestRow>) => void;
  onRemove: () => void;
  strings: Strings;
}) {
  return (
    <div className={`${CARD} space-y-4`}>
      <div className="flex items-center justify-between gap-3">
        <h3 className={WIDGET_H3} id={`act-title-${row.id}`}>
          {t(strings.testLabel, { n: index + 1 })}
        </h3>
        <button
          aria-label={t(strings.removeTestAria, { n: index + 1 })}
          className={BUTTON_SMALL}
          disabled={!canRemove}
          onClick={onRemove}
          type="button"
        >
          {strings.removeTest}
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          id={`act-date-${row.id}`}
          label={strings.dateLabel}
          onChange={(date) => onChange({ date })}
          type="date"
          value={row.date}
        />
        <SelectField
          id={`act-version-${row.id}`}
          label={strings.versionLabel}
          onChange={(version) => onChange({ version })}
          options={[
            { value: "enhanced", label: strings.versions.enhanced },
            { value: "legacy", label: strings.versions.legacy },
          ]}
          value={row.version}
        />
      </div>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        {SECTIONS.map((section) => (
          <TextField
            id={`act-${section}-${row.id}`}
            key={section}
            label={strings.sections[section]}
            numeric
            onChange={(value) => onChange({ [section]: value })}
            value={row[section]}
          />
        ))}
      </div>
    </div>
  );
}

export default function ActScoreCalculatorWidget() {
  const strings = useToolUi(ACT_SCORE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const idCounter = useRef(INITIAL_ROWS.length);
  const [rows, setRows] = useState<TestRow[]>(INITIAL_ROWS);

  const parsed = rows.map(readTest);
  const valid = parsed.flatMap((test, index) =>
    test === null ? [] : [{ test, index }]
  );
  const superscore = actSuperscore(valid.map((entry) => entry.test));
  const label = (index: number) => {
    const row = rows[index];
    const n = fmt(index + 1);
    return row?.date
      ? t(strings.testWithDate, { n, date: formatDate(row.date, locale) })
      : n;
  };

  function addRow() {
    idCounter.current += 1;
    const id = idCounter.current;
    setRows((all) => [
      ...all,
      {
        id,
        date: "",
        version: "enhanced",
        english: "",
        math: "",
        reading: "",
        science: "",
      },
    ]);
  }

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <h2 className={WIDGET_H2}>{strings.testsHeading}</h2>
        <p className="text-muted-foreground text-sm">{strings.versionsHint}</p>
      </div>
      {rows.map((row, index) => (
        <TestEditor
          canRemove={rows.length > 1}
          index={index}
          key={row.id}
          onChange={(patch) =>
            setRows((all) =>
              all.map((each) =>
                each.id === row.id ? { ...each, ...patch } : each
              )
            )
          }
          onRemove={() =>
            setRows((all) => all.filter((each) => each.id !== row.id))
          }
          row={row}
          strings={strings}
        />
      ))}
      <button className={BUTTON_SECONDARY} onClick={addRow} type="button">
        {strings.addTest}
      </button>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {rows.map((row, index) =>
          parsed[index] === null ? (
            <Notice key={row.id}>{t(strings.skipped, { n: index + 1 })}</Notice>
          ) : null
        )}
        {superscore ? (
          <SuperscorePanel
            fmt={fmt}
            label={(entry) => label(valid[entry]?.index ?? entry)}
            strings={strings}
            superscore={superscore}
          />
        ) : (
          <Notice>{strings.enterTests}</Notice>
        )}
      </section>

      {valid.length > 0 ? (
        <section className="space-y-3">
          <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
          <DataTable
            head={[
              strings.colTest,
              strings.colVersion,
              strings.colComposite,
              strings.colStem,
            ]}
            rows={valid.map(({ test, index }) => {
              const composite = actComposite(test);
              const stem = actStem(test);
              return [
                label(index),
                strings.versionShort[test.version],
                composite === null ? strings.needsScience : fmt(composite),
                stem === null ? "—" : fmt(stem),
              ];
            })}
          />
        </section>
      ) : null}
    </div>
  );
}
